import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTradeGroupRanges, LadderRangeEntry } from '../api/tradeGroups';
import { formatDollar } from '../utils/currencyFormatter';
import './LadderRangesTable.css';

interface LadderRangesTableProps {
  tradeGroupId: string;
  productId: string;
}

const LadderRangesTable: React.FC<LadderRangesTableProps> = ({ tradeGroupId, productId }) => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<keyof LadderRangeEntry>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trade-group-ranges', tradeGroupId, statusFilter],
    queryFn: () => getTradeGroupRanges(tradeGroupId, statusFilter || undefined),
    staleTime: 30000, // Cache for 30 seconds
  });

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return formatDollar(value, productId);
  };

  const formatTimestamp = (timestamp: number | null): string => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatPriceRange = (entry: LadderRangeEntry): React.JSX.Element => {
    const width = entry.rangeMaxPrice - entry.rangeMinPrice;
    return (
      <div className="price-range-container">
        <span className="price-range-values">
          {formatCurrency(entry.rangeMinPrice)} - {formatCurrency(entry.rangeMaxPrice)}
        </span>
        <span className="price-range-width">({formatCurrency(width)})</span>
      </div>
    );
  };

  const getRangeStatusBadge = (status: string): React.JSX.Element => {
    const config = {
      ACTIVE: { label: 'Active', className: 'badge-active' },
      APPROACHING_AGEOUT: { label: 'Approaching Age-Out', className: 'badge-approaching' },
      AGED_OUT: { label: 'Aged Out', className: 'badge-aged-out' },
    };

    const { label, className } = config[status as keyof typeof config] || { label: status, className: 'badge-default' };

    return <span className={`range-status-badge ${className}`}>{label}</span>;
  };

  const getCreationReasonBadge = (reason: string): React.JSX.Element => {
    const config = {
      INITIAL: { label: 'Initial', className: 'reason-initial' },
      UPWARD_ADJUSTMENT: { label: 'Upward Adjustment', className: 'reason-upward' },
      DOWNWARD_ADJUSTMENT: { label: 'Downward Adjustment', className: 'reason-downward' },
      PRICE_RETURN: { label: 'Price Return', className: 'reason-return' },
    };

    const { label, className } = config[reason as keyof typeof config] || { label: reason, className: 'reason-default' };

    return <span className={`creation-reason-badge ${className}`}>{label}</span>;
  };

  const formatAgeOutThreshold = (threshold: number, status: string): React.JSX.Element => {
    const now = Date.now();
    const timeRemaining = threshold - now;

    if (status === 'AGED_OUT') {
      return <span className="age-out-text">{formatTimestamp(threshold)}</span>;
    }

    if (status === 'APPROACHING_AGEOUT' || (status === 'ACTIVE' && timeRemaining < 24 * 60 * 60 * 1000)) {
      const hours = Math.floor(timeRemaining / (60 * 60 * 1000));
      const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
      
      return (
        <div className="age-out-container">
          <span className="age-out-text">{formatTimestamp(threshold)}</span>
          {timeRemaining > 0 && (
            <span className="age-out-countdown">({hours}h {minutes}m remaining)</span>
          )}
        </div>
      );
    }

    return <span className="age-out-text">{formatTimestamp(threshold)}</span>;
  };

  const handleSort = (column: keyof LadderRangeEntry) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc'); // Default to ascending when changing columns
    }
  };

  const getSortedRanges = (ranges: LadderRangeEntry[]): LadderRangeEntry[] => {
    return [...ranges].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      return 0;
    });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  if (isLoading) {
    return (
      <div className="ladder-ranges-loading">
        <div className="spinner"></div>
        <p>Loading ladder ranges...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="ladder-ranges-error">
        <div className="error-icon">⚠️</div>
        <p className="error-message">Failed to load ladder ranges</p>
        <p className="error-detail">{error instanceof Error ? error.message : 'Unknown error'}</p>
        <button className="retry-button" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const ranges = data?.ranges || [];
  const summary = data?.summary;
  const sortedRanges = getSortedRanges(ranges);

  if (ranges.length === 0 && !statusFilter) {
    return (
      <div className="ladder-ranges-empty">
        <div className="empty-icon">📊</div>
        <p className="empty-message">No ranges yet</p>
        <p className="empty-description">
          Ranges will appear here once the bot creates price ladders.
        </p>
      </div>
    );
  }

  return (
    <div className="ladder-ranges-container">
      {/* Summary Section */}
      {summary && (
        <div className="ladder-ranges-summary">
          <div className="summary-card">
            <div className="summary-label">Total Ranges</div>
            <div className="summary-value">{summary.totalRanges}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Active Ranges</div>
            <div className="summary-value active-count">{summary.activeRanges}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Deployed Capital</div>
            <div className="summary-value">{formatCurrency(summary.totalDeployedCapital)}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Average Range Width</div>
            <div className="summary-value">{formatCurrency(summary.averageRangeWidth)}</div>
          </div>
        </div>
      )}

      {/* Filter Controls */}
      <div className="ladder-ranges-controls">
        <div className="filter-group">
          <label htmlFor="statusFilter">Filter by Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="APPROACHING_AGEOUT">Approaching Age-Out</option>
            <option value="AGED_OUT">Aged Out</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {sortedRanges.length === 0 ? (
        <div className="ladder-ranges-empty">
          <div className="empty-icon">🔍</div>
          <p className="empty-message">No ranges match the selected filter</p>
          <p className="empty-description">Try selecting a different status.</p>
        </div>
      ) : (
        <div className="ladder-ranges-table-wrapper">
          <table className="ladder-ranges-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('createdAt')} className="sortable">
                  Created At {sortColumn === 'createdAt' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('creationReason')} className="sortable">
                  Creation Reason {sortColumn === 'creationReason' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('rangeMinPrice')} className="sortable">
                  Price Range {sortColumn === 'rangeMinPrice' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('deployedCapital')} className="sortable">
                  Deployed Capital {sortColumn === 'deployedCapital' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('rangeStatus')} className="sortable">
                  Status {sortColumn === 'rangeStatus' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('lastFillTime')} className="sortable">
                  Last Fill {sortColumn === 'lastFillTime' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('ageOutThreshold')} className="sortable">
                  Age Out {sortColumn === 'ageOutThreshold' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRanges.map((entry) => (
                <tr 
                  key={entry.rangeId}
                  className={entry.rangeStatus === 'ACTIVE' ? 'active-range-row' : ''}
                >
                  <td className="timestamp-cell">{formatTimestamp(entry.createdAt)}</td>
                  <td className="reason-cell">{getCreationReasonBadge(entry.creationReason)}</td>
                  <td className="price-range-cell">{formatPriceRange(entry)}</td>
                  <td className="capital-cell">{formatCurrency(entry.deployedCapital)}</td>
                  <td className="status-cell">{getRangeStatusBadge(entry.rangeStatus)}</td>
                  <td className="timestamp-cell">{formatTimestamp(entry.lastFillTime)}</td>
                  <td className="age-out-cell">{formatAgeOutThreshold(entry.ageOutThreshold, entry.rangeStatus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LadderRangesTable;
